import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import CollapsibleLayout from '@/components/CollapsibleLayout';
import { getAnalytics } from '@/utils/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { CheckCircle2, Clock, AlertCircle, Package, TrendingUp, Users } from 'lucide-react';
import { toast } from 'sonner';

// Theme colors - Navy and Gold
const CHART_COLORS = ['hsl(220, 60%, 20%)', 'hsl(45, 90%, 50%)', '#3b82f6', '#10b981', '#f59e0b', '#ef4444'];

const StatCard = ({ title, value, icon, color }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-white rounded-lg border border-border p-5"
  >
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-muted-foreground">{title}</p>
        <p className="text-2xl font-bold text-foreground mt-1">{value}</p>
      </div>
      <div 
        className="p-3 rounded-lg"
        style={{ backgroundColor: color }}
      >
        <div className="text-white">{icon}</div>
      </div>
    </div>
  </motion.div>
);

const AdminDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await getAnalytics();
      setAnalytics(response.data);
    } catch (error) {
      if (error.response?.status !== 401) toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <CollapsibleLayout>
        <div className="flex items-center justify-center h-96 bg-background">
          <div className="text-muted-foreground">Loading analytics...</div>
        </div>
      </CollapsibleLayout>
    );
  }

  return (
    <CollapsibleLayout>
      <div className="h-screen bg-background overflow-auto">
        <div className="max-w-7xl mx-auto p-6 space-y-6">
          {/* Header */}
          <div className="mb-2">
            <h1 className="text-2xl font-bold text-foreground">
              Analytics Dashboard
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Monitor product development performance and team workload
            </p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              title="Total Tasks"
              value={analytics?.total_tasks || 0}
              icon={<Package size={20} />}
              color="hsl(220, 60%, 20%)"
            />
            <StatCard
              title="Completed"
              value={analytics?.completed_tasks || 0}
              icon={<CheckCircle2 size={20} />}
              color="#10b981"
            />
            <StatCard
              title="In Progress"
              value={analytics?.in_progress_tasks || 0}
              icon={<Clock size={20} />}
              color="hsl(45, 90%, 50%)"
            />
            <StatCard
              title="On Hold"
              value={analytics?.on_hold_tasks || 0}
              icon={<AlertCircle size={20} />}
              color="#ef4444"
            />
          </div>

          {/* Metrics Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-lg border border-border p-5"
              data-testid="avg-turnaround-card"
            >
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp size={18} style={{ color: 'hsl(45, 90%, 50%)' }} />
                <h3 className="text-sm font-semibold text-foreground">Average Metrics</h3>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Avg. Turnaround Time</p>
                  <p className="text-xl font-bold text-foreground">
                    {analytics?.avg_turnaround_time ? `${analytics.avg_turnaround_time.toFixed(1)} days` : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Avg. Hours per Task</p>
                  <p className="text-xl font-bold text-foreground">
                    {analytics?.avg_hours_per_task ? `${analytics.avg_hours_per_task.toFixed(1)} hrs` : 'N/A'}
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-lg border border-border p-5"
              data-testid="designer-workload-card"
            >
              <div className="flex items-center gap-2 mb-4">
                <Users size={18} style={{ color: 'hsl(45, 90%, 50%)' }} />
                <h3 className="text-sm font-semibold text-foreground">Designer Workload</h3>
              </div>
              <div className="space-y-2">
                {analytics?.designer_workload?.length > 0 ? (
                  analytics.designer_workload.slice(0, 4).map((designer) => (
                    <div key={designer.designer_id} className="flex justify-between items-center">
                      <span className="text-sm text-foreground">{designer.designer_name}</span>
                      <span 
                        className="px-2 py-0.5 rounded-full text-xs font-medium"
                        style={{ backgroundColor: 'hsl(45, 90%, 90%)', color: 'hsl(220, 60%, 20%)' }}
                      >
                        {designer.active_tasks} active
                      </span>
                    </div>
                  ))
                ) : (
                  <p className="text-muted-foreground text-sm">No active workload</p>
                )}
              </div>
            </motion.div>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-lg border border-border p-5"
              data-testid="category-chart"
            >
              <h3 className="text-sm font-semibold text-foreground mb-4">Category Distribution</h3>
              {analytics?.category_distribution?.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={analytics.category_distribution}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(220, 20%, 90%)" />
                    <XAxis dataKey="category" stroke="hsl(220, 10%, 50%)" fontSize={11} />
                    <YAxis stroke="hsl(220, 10%, 50%)" fontSize={11} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'white', 
                        border: '1px solid hsl(220, 20%, 90%)',
                        borderRadius: '6px',
                        fontSize: '12px'
                      }} 
                    />
                    <Bar dataKey="count" fill="hsl(220, 60%, 20%)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-muted-foreground text-sm text-center py-16">No data available</p>
              )}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-lg border border-border p-5"
              data-testid="status-chart"
            >
              <h3 className="text-sm font-semibold text-foreground mb-4">Task Status Overview</h3>
              {analytics ? (
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Completed', value: analytics.completed_tasks || 0 },
                        { name: 'In Progress', value: analytics.in_progress_tasks || 0 },
                        { name: 'On Hold', value: analytics.on_hold_tasks || 0 },
                        { name: 'Revision', value: analytics.revision_tasks || 0 },
                        { name: 'Other', value: Math.max(0, (analytics.total_tasks || 0) - (analytics.completed_tasks || 0) - (analytics.in_progress_tasks || 0) - (analytics.on_hold_tasks || 0) - (analytics.revision_tasks || 0)) }
                      ].filter(d => d.value > 0)}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => percent > 0.05 ? `${name}: ${(percent * 100).toFixed(0)}%` : ''}
                      outerRadius={90}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {CHART_COLORS.map((color, index) => (
                        <Cell key={`cell-${index}`} fill={color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'white', 
                        border: '1px solid hsl(220, 20%, 90%)',
                        borderRadius: '6px',
                        fontSize: '12px'
                      }} 
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-muted-foreground text-sm text-center py-16">No data available</p>
              )}
            </motion.div>
          </div>

          {/* Recent Completions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-white rounded-lg border border-border p-5"
          >
            <h3 className="text-sm font-semibold text-foreground mb-3">Recent Completions</h3>
            <p className="text-muted-foreground text-sm">Recent completed tasks will appear here</p>
          </motion.div>
        </div>
      </div>
    </CollapsibleLayout>
  );
};

export default AdminDashboard;
